import { MoneyValueDto } from '@/types/MoneyValueDto.ts';
import { StatusTone } from '@/components/ui/statusTone.ts';
import { ChangePackageStatus } from '@/features/dashboard/bookingChanges/types/ChangeViewModel.ts';
import { formatSignedMoneyList } from '@/features/dashboard/bookingChanges/utils/formatters.ts';
import { isZeroMoneyList } from '@/features/dashboard/bookingChanges/utils/money.ts';
import { EMPTY_VALUE } from '@/components/ui/EmptyValue.tsx';

/**
 * Etykiety stanów paczki i powodów, dla których edycja bywa zamknięta.
 * Jedyne miejsce, w którym te napisy istnieją — komponenty nigdy nie budują
 * własnych map statusów (reguła z `CLAUDE.md`).
 */

export const CHANGE_PACKAGE_STATUS_LABELS: Record<ChangePackageStatus, string> =
  {
    [ChangePackageStatus.DRAFT]: 'Szkic',
    [ChangePackageStatus.AWAITING_CLIENT]: 'Czeka na akceptację',
    [ChangePackageStatus.APPLIED]: 'Wprowadzona',
    [ChangePackageStatus.REJECTED]: 'Odrzucona',
    [ChangePackageStatus.CANCELLED]: 'Anulowana',
  };

export const getChangePackageStatusLabel = (
  status: ChangePackageStatus,
): string => CHANGE_PACKAGE_STATUS_LABELS[status];

/**
 * Ton odznaki stanu paczki. Anulowana stoi szaro, a nie na czerwono: zdjęcie
 * własnego szkicu to zwykłe wyjście z edycji, nie awaria — czerwień zostaje
 * dla odmowy klienta.
 */
export const CHANGE_PACKAGE_STATUS_TONE: Record<
  ChangePackageStatus,
  StatusTone
> = {
  [ChangePackageStatus.DRAFT]: 'neutral',
  [ChangePackageStatus.AWAITING_CLIENT]: 'warning',
  [ChangePackageStatus.APPLIED]: 'success',
  [ChangePackageStatus.REJECTED]: 'error',
  [ChangePackageStatus.CANCELLED]: 'neutral',
};

export const getChangePackageStatusTone = (
  status: ChangePackageStatus,
): StatusTone => CHANGE_PACKAGE_STATUS_TONE[status];

/**
 * Kwota paczki w historii. `null` dostaje kreskę, a nie „0,00 zł”: kontrakt
 * wypełnia ją wyłącznie dla paczki wprowadzonej, więc przy anulowanej
 * i odrzuconej kwoty **nie ma**, a zero mówiłoby, że paczka nic nie ruszyła.
 *
 * Napis, a nie `MoneyAmountList`: obie odpowiedzi bezkwotowe są słowami, a ta
 * sama etykieta wchodzi w dopisek karty „Ostatnie zmiany”, sklejany z kropek.
 * Paczka wielowalutowa wymienia obie kwoty.
 */
export const getChangeImpactLabel = (
  impact: MoneyValueDto[] | null,
): string => {
  if (impact === null) {
    return EMPTY_VALUE;
  }
  return isZeroMoneyList(impact) ? 'bez wpływu' : formatSignedMoneyList(impact);
};

/**
 * Dlaczego tryb edycji jest niedostępny przy paczce w danym stanie.
 *
 * `null` znaczy „edycja wolna”. Blokuje wyłącznie paczka wysłana klientowi:
 * dopóki nie odpowie, zmiana jej zawartości rozjechałaby to, co widzi on,
 * z tym, co widzi operator (projekt W3: „edycja jest zablokowana do czasu
 * decyzji klienta”).
 */
export const CHANGE_EDIT_LOCK_REASONS: Record<
  ChangePackageStatus,
  string | null
> = {
  [ChangePackageStatus.DRAFT]: null,
  [ChangePackageStatus.AWAITING_CLIENT]:
    'Paczka czeka na decyzję zapisującego — edycja będzie możliwa po jego odpowiedzi.',
  [ChangePackageStatus.APPLIED]: null,
  [ChangePackageStatus.REJECTED]: null,
  [ChangePackageStatus.CANCELLED]: null,
};

export const getChangeEditLockReason = (
  status: ChangePackageStatus,
): string | null => CHANGE_EDIT_LOCK_REASONS[status];
